/** Tabbed create/edit dialog for one access group template.
 *
 * Tabs: General (name, codename, group, parent, role flags) · Arguments ·
 * Permissions (scope templates) · Managed groups (tenant-scoped only).
 * Saving persists everything in one nested payload; saving a TENANT-scoped
 * template also propagates to its managed access groups — the propagation
 * summary/warnings are shown before the dialog closes.
 */
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { accessGroupsApi } from '@/api/accessGroups';
import {
  rbacApi,
  ROLE_LABELS,
  type AgroupTemplateWriteInput,
  type PropagationResult,
  type RoleFlag,
  type TemplateScope,
} from '@/api/rbac';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { accessGroupActions, rbacOperatorActions } from '@/features/users/userLinks';
import { usePermissions } from '@/hooks/usePermissions';

import { TemplatePermissionsEditor, type ScopeRow } from './TemplatePermissionsEditor';
import { WarningList } from './WarningList';

interface ArgRow {
  id?: number;
  alias: string;
  referenced_by: string[];
}

interface Props {
  templateId: number | null; // null = create
  scope: TemplateScope;
  /** Viewers without `console_rbac_manage` open the dialog to READ it: every
   *  input is disabled and Save is hidden. Reading a template is how you find
   *  out why a managed group has the permissions it has. */
  readOnly?: boolean;
  onClose: () => void;
}

export function TemplateEditDialog({ templateId, scope, readOnly = false, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const canViewHistory = hasPerm('console_audit_view');
  const topId = scope === 'system' ? null : scope;

  const [tab, setTab] = useState(0);
  const [name, setName] = useState('');
  const [codename, setCodename] = useState('');
  const [groupId, setGroupId] = useState<number | ''>('');
  const [parentId, setParentId] = useState<number | ''>('');
  const [role, setRole] = useState<RoleFlag>(0);
  const [propagatedRole, setPropagatedRole] = useState<RoleFlag>(0);
  const [args, setArgs] = useState<ArgRow[]>([]);
  const [scopes, setScopes] = useState<ScopeRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [propagation, setPropagation] = useState<PropagationResult | null>(null);

  const detailQuery = useQuery({
    queryKey: ['agroupTemplate', templateId],
    queryFn: () => rbacApi.getTemplate(templateId!),
    enabled: templateId !== null,
  });
  const groupsQuery = useQuery({
    queryKey: ['agroupTemplateGroups', 'all'],
    queryFn: () => rbacApi.listGroups({ pageSize: 200 }),
  });
  const permsQuery = useQuery({
    queryKey: ['employeePermissions'],
    queryFn: () => rbacApi.listEmployeePermissions(),
  });
  const siblingsQuery = useQuery({
    queryKey: ['agroupTemplates', scope, 'group', groupId],
    queryFn: () => rbacApi.listTemplates({ scope, group: groupId as number }),
    enabled: groupId !== '',
  });

  // Hydrate form state from the loaded template exactly once.
  useEffect(() => {
    const tpl = detailQuery.data;
    if (templateId === null || !tpl || loaded || !permsQuery.data) return;
    setName(tpl.name);
    setCodename(tpl.codename);
    setGroupId(tpl.group);
    setParentId(tpl.parent ?? '');
    setRole(tpl.role);
    setPropagatedRole(tpl.propagated_role);
    setArgs(tpl.arguments.map((a) => ({ id: a.id, alias: a.alias, referenced_by: a.referenced_by })));
    const catalog = new Map(permsQuery.data.results.map((p) => [p.id, p]));
    setScopes(
      tpl.scope_templates.map((st) => ({
        id: st.id,
        perm: st.perm.codename,
        permInfo: catalog.get(st.perm.id) ?? st.perm,
        argument_aliases: st.argument_aliases,
        all_tags: st.all_tags,
        target_template_ids: st.target_template_ids,
        target_guest_group: st.target_guest_group,
      })),
    );
    setLoaded(true);
  }, [detailQuery.data, templateId, loaded, permsQuery.data]);

  const siblings = useMemo(
    () => (siblingsQuery.data?.results ?? []).filter((t) => t.id !== templateId),
    [siblingsQuery.data, templateId],
  );
  const aliases = args.map((a) => a.alias).filter(Boolean);
  const isTenant = topId !== null;

  const managedGroupsQuery = useQuery({
    queryKey: ['accessGroups', topId],
    queryFn: () => accessGroupsApi.list(topId!),
    enabled: isTenant && templateId !== null,
  });
  const managedGroups = (managedGroupsQuery.data?.results ?? []).filter(
    (g) => g.template?.id === templateId,
  );

  const saveMutation = useMutation({
    mutationFn: () => {
      const body: AgroupTemplateWriteInput = {
        group: groupId as number,
        name: name.trim(),
        codename: codename.trim(),
        parent: parentId === '' ? null : parentId,
        role,
        propagated_role: propagatedRole,
        arguments: args.map((a, i) => ({ id: a.id, position: i, alias: a.alias.trim() })),
        scope_templates: scopes.map(({ permInfo: _permInfo, ...rest }) => rest),
      };
      if (templateId === null) {
        return rbacApi.createTemplate({ ...body, tour_operator: topId });
      }
      return rbacApi.updateTemplate(templateId, body);
    },
    onSuccess: (tpl) => {
      qc.invalidateQueries({ queryKey: ['agroupTemplates'] });
      qc.invalidateQueries({ queryKey: ['agroupTemplate', templateId] });
      qc.invalidateQueries({ queryKey: ['accessGroups'] });
      if (tpl.propagation) {
        setPropagation(tpl.propagation);
        snackbar.show(
          `Saved; ${tpl.propagation.updated_group_ids.length} managed group(s) updated`,
          tpl.propagation.warnings.length ? 'warning' : 'success',
        );
        if (!tpl.propagation.warnings.length) onClose();
      } else {
        snackbar.show('Template saved', 'success');
        onClose();
      }
    },
    onError: (e) => snackbar.show((e as Error).message, 'error'),
  });

  const manageMutation = useMutation({
    mutationFn: ({ id, managed }: { id: number; managed: boolean }) =>
      accessGroupsApi.manage(topId!, id, managed),
    onSuccess: (detail) => {
      qc.invalidateQueries({ queryKey: ['accessGroups'] });
      const warnings = detail.warnings ?? [];
      snackbar.show(
        `Group is now ${detail.managed ? 'managed' : 'unmanaged'}`,
        warnings.length ? 'warning' : 'success',
      );
    },
    onError: (e) => snackbar.show((e as Error).message, 'error'),
  });

  const canSave =
    name.trim().length > 0 &&
    codename.trim().length > 0 &&
    groupId !== '' &&
    !saveMutation.isPending &&
    (templateId === null || loaded);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {templateId === null ? 'New template' : `Edit template “${detailQuery.data?.name ?? ''}”`}
        {isTenant && detailQuery.data?.tour_operator && (
          <Typography variant="body2" color="text.secondary">
            {'Tenant-scoped: '}
            <ValueActions actions={rbacOperatorActions(detailQuery.data.tour_operator)}>
              {detailQuery.data.tour_operator.name}
            </ValueActions>
            {readOnly
              ? ' — read-only (you cannot manage RBAC)'
              : ' — saving propagates to its managed groups'}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {propagation && (
          <Alert severity={propagation.warnings.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
            <Typography variant="body2">
              Propagated to {propagation.updated_group_ids.length} group(s);{' '}
              {propagation.skipped_group_ids.length} skipped (unmanaged / incomplete bindings).
            </Typography>
            <WarningList warnings={propagation.warnings} />
          </Alert>
        )}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="General" />
          <Tab label={`Arguments (${args.length})`} />
          <Tab label={`Permissions (${scopes.length})`} />
          {isTenant && templateId !== null && <Tab label={`Managed groups (${managedGroups.length})`} />}
          {canViewHistory && templateId !== null && <Tab value={9} label="History" />}
        </Tabs>

        {tab === 0 && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                disabled={readOnly}
                inputProps={{ autoComplete: 'off' }}
              />
              <TextField
                label="Code name"
                value={codename}
                onChange={(e) => setCodename(e.target.value)}
                required
                fullWidth
                disabled={readOnly}
                helperText="Seeds the generated groups' code names"
                inputProps={{ autoComplete: 'off' }}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Template group"
                value={groupId}
                onChange={(e) => setGroupId(Number(e.target.value))}
                disabled={readOnly || templateId !== null} // immutable after create
                fullWidth
              >
                {(groupsQuery.data?.results ?? []).map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Parent template"
                value={parentId}
                onChange={(e) => setParentId(e.target.value === '' ? '' : Number(e.target.value))}
                fullWidth
                disabled={readOnly}
                helperText="Child templates must declare all of the parent's argument aliases"
              >
                <MenuItem value="">None</MenuItem>
                {siblings.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              {([
                ['Role', role, setRole],
                ['Propagated role', propagatedRole, setPropagatedRole],
              ] as const).map(([label, value, setter]) => (
                <TextField
                  key={label}
                  select
                  label={label}
                  value={value}
                  onChange={(e) => setter(Number(e.target.value) as RoleFlag)}
                  fullWidth
                  disabled={readOnly}
                  helperText={
                    label === 'Role'
                      ? 'Resolved to the tenant’s legacy role at materialisation'
                      : 'Assigned to members of the generated groups'
                  }
                >
                  {Object.entries(ROLE_LABELS).map(([v, l]) => (
                    <MenuItem key={v} value={Number(v)}>{l}</MenuItem>
                  ))}
                </TextField>
              ))}
            </Stack>
          </Stack>
        )}

        {tab === 1 && (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Arguments are access-tag placeholders: one access group is generated per combination of
              argument values. Aliases are the matching key across templates of the same group.
            </Typography>
            {args.map((arg, i) => (
              <Stack key={arg.id ?? `new-${i}`} direction="row" spacing={2} alignItems="center">
                <TextField
                  size="small"
                  label={`Argument ${i + 1} alias`}
                  value={arg.alias}
                  onChange={(e) =>
                    setArgs(args.map((a, j) => (j === i ? { ...a, alias: e.target.value } : a)))
                  }
                  placeholder={`arg${i + 1}`}
                  disabled={readOnly}
                  inputProps={{ autoComplete: 'off' }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {arg.referenced_by.length
                    ? `Referenced by: ${arg.referenced_by.join(', ')}`
                    : 'Not referenced by any permission'}
                </Typography>
                <Tooltip
                  title={
                    arg.referenced_by.length
                      ? 'Remove the referencing permissions first'
                      : 'Remove argument'
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={
                        readOnly ||
                        arg.referenced_by.length > 0 ||
                        scopes.some((s) => s.argument_aliases.includes(arg.alias))
                      }
                      onClick={() => setArgs(args.filter((_, j) => j !== i))}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))}
            {!readOnly && (
              <Button
                size="small"
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => setArgs([...args, { alias: `arg${args.length + 1}`, referenced_by: [] }])}
              >
                Add argument
              </Button>
            )}
          </Stack>
        )}

        {tab === 2 && (
          <TemplatePermissionsEditor
            permCatalog={permsQuery.data?.results ?? []}
            aliases={aliases}
            groupTemplates={siblings}
            scopes={scopes}
            onChange={setScopes}
            readOnly={readOnly}
          />
        )}

        {tab === 3 && isTenant && templateId !== null && (
          <Stack spacing={1}>
            {managedGroups.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No access groups reference this template yet — materialise its group for the tenant.
              </Typography>
            )}
            {managedGroups.map((g) => (
              <Stack key={g.id} direction="row" spacing={2} alignItems="center">
                <Typography sx={{ minWidth: 260 }}>
                  <ValueActions actions={accessGroupActions(g, topId)}>{g.name}</ValueActions>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                  {g.member_count} member(s)
                </Typography>
                <Tooltip title={g.managed ? 'Unmanage: freeze and make editable' : 'Manage: overwrite with template state'}>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Switch
                      size="small"
                      checked={g.managed}
                      disabled={readOnly || manageMutation.isPending}
                      onChange={(e) => manageMutation.mutate({ id: g.id, managed: e.target.checked })}
                    />
                    <Typography variant="caption">{g.managed ? 'managed' : 'unmanaged'}</Typography>
                  </Box>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}

        {tab === 9 && templateId !== null && (
          <HistoryPanel
            contentType="demo.access_group_template"
            objectPk={String(templateId)}
            onRestored={() => void qc.invalidateQueries({ queryKey: ['agroupTemplate', templateId] })}
          />
        )}
      </DialogContent>
      <DialogActions>
        {tab === 9 || readOnly ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>{propagation ? 'Close' : 'Cancel'}</Button>
            <Button variant="contained" disabled={!canSave} onClick={() => saveMutation.mutate()}>
              {isTenant && templateId !== null ? 'Save & propagate' : 'Save'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
